/**
 * Review tags (owner 10-21: "admin will have more tags and the executives less"). The executive sees the
 * state of his own request; the admin sees how many requests are open per template. Text, not colour only.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ReviewState } from '@saledeed/shared';
import { useCan } from '../auth/session';
import { Badge } from '../components/ui';
import { reviewCounts } from './api';

export const REVIEW_TAG: Record<ReviewState, { text: string; tone: 'amber' | 'blue' | 'green' }> = {
  open: { text: 'Review asked', tone: 'amber' },
  answered: { text: 'Admin replied', tone: 'blue' },
  done: { text: 'Done', tone: 'green' },
};

export function ReviewTag({ state }: { state: ReviewState }) {
  return <Badge tone={REVIEW_TAG[state].tone}>{REVIEW_TAG[state].text}</Badge>;
}

/** Open review requests per template, for admins (react-query key `['review-counts']`). */
export function useReviewCounts() {
  const admin = useCan('review.answer');
  return useQuery({ queryKey: ['review-counts'], queryFn: reviewCounts, enabled: admin });
}

/** Admin: "N reviews" next to a template, linking to Activity → Reviews filtered to it. Nothing when none is open. */
export function ReviewCountBadge({ templateCode }: { templateCode: string }) {
  const counts = useReviewCounts();
  const open = counts.data?.[templateCode]?.open ?? 0;
  if (!open) return null;
  // Router Link (review fix 10-21): no full page reload. ActivityPage reads tab/templateCode from the address.
  return (
    <Link to="/admin/activity" search={{ tab: 'reviews', templateCode }}
      className="inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900 underline"
      title="Executives asked about this template: read and answer in Activity → Reviews">
      {open === 1 ? '1 review' : `${open} reviews`}
    </Link>
  );
}
